import React, { useState } from 'react';
import classnames from 'classnames';

import { Fab, ButtonBase, Divider, Menu, MenuList, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, MenuItem, Translate } from '@hydra/common/components';
import { useReviewContext } from '@hydra/common/context';

import mapModuleToIcon from '@hydra/utils/mapModuleToIcon';
import { ReviewStructure } from '@hydra/utils/reviewStructure';
import { sendEvent } from '@hydra/utils/gtm';

const useStyles = makeStyles(theme => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(3),
    background: theme.palette.grey[500],
    borderRadius: '50%'
  },
  smUpHidden: {
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  hide: { display: 'none !important' },
  show: { display: 'block' },
  sectionTitleMenu: {
    '&:hover': {
      color: 'inherit'
    }
  },
  sectionTitle: {
    fontWeight: 'bold'
  },
  moduleTitle: {
    display: 'flex',
    '&:hover': {
      '& svg': {
        color: theme.palette.primary.main
      }
    }
  },
  floatingButton: {
    boxShadow: '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%)',
    padding: theme.spacing(1)
  },
  icon: {
    marginRight: theme.spacing(1),
    verticalAlign: 'sub'
  }
}));

interface FloatingReviewNavProps {
  showModule: ({ module, section, onpageReviewFilter }: { module: string, section: string, onpageReviewFilter?: string | null }) => boolean;
  showSection: ({ section, onpageReviewFilter }: { section: string, onpageReviewFilter?: string | null }) => boolean;
  reviewStructure: ReviewStructure;
  isExtension: boolean;
  isAssistant: boolean;
  isLeadgen: boolean;
}

export function FloatingReviewNav ({ showModule, showSection, reviewStructure, isExtension, isAssistant, isLeadgen }: FloatingReviewNavProps) {
  const classes = useStyles();
  const { isShopify } = useReviewContext();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const showSections = {
    on_page: showSection({ section: 'on_page' }),
    off_page: showSection({ section: 'off_page' })
  };

  function handleClick (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    sendEvent('review-anchornav-open-btn');
    setAnchorEl(event.currentTarget);
  }

  function handleClose () {
    setAnchorEl(null);
  }

  return (
    <>
      <Fab
        className={classnames(
          classes.fab,
          !showSections.on_page && !showSections.off_page && classes.hide,
          !isExtension && !isAssistant && !isLeadgen && classes.smUpHidden
        )}
        color='secondary'
        size='small'
        onClick={handleClick}
      >
        <Icon type='list-ul' color='white' />
      </Fab>
      <Menu
        disableScrollLock
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        PaperProps={{
          style: {
            bottom: '60px'
          }
        }}
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuList disablePadding>
          {Object.keys(reviewStructure).map((section, index) => (
            <div key={index} className={showSections[section as keyof ReviewStructure] ? classes.show : classes.hide}>
              <MenuItem
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  sendEvent('review-anchornav-category-link', { category: section });
                  if (isShopify) {
                    e.preventDefault();
                    document.getElementById(`module-section-title-${section}`)?.scrollIntoView();
                  }
                  handleClose();
                }}
                component={ButtonBase}
                href={`#module-section-title-${section}`}
                className={classes.sectionTitleMenu}
              >
                <Typography className={classes.sectionTitle}>
                  <Translate content={`wip_section_${section}`} />
                </Typography>
              </MenuItem>
              {reviewStructure[section as keyof ReviewStructure] && Object.keys(reviewStructure[section as keyof ReviewStructure]).map(module => (
                <MenuItem
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    sendEvent('review-anchornav-category-link', { category: module });
                    if (isShopify) {
                      e.preventDefault();
                      document.getElementById(`module-${module}`)?.scrollIntoView();
                    }
                    handleClose();
                  }}
                  component={ButtonBase}
                  key={module}
                  href={`#module-${module}`}
                  className={classnames(classes.moduleTitle, !showModule({ module, section }) && classes.hide)}
                >
                  <Icon className={classes.icon} type={mapModuleToIcon(module)} size='xs' />
                  <Translate content={module} />
                </MenuItem>
              ))}
              {
                (Object.keys(reviewStructure).length < index + 1) && <Divider />
              }
            </div>
          ))}
        </MenuList>
      </Menu>
    </>
  );
}

export default React.memo(FloatingReviewNav);
